import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { pluck } from 'rxjs/operators';
import { Project } from 'src/app/core/objects/project';
import { ProjectApiService } from 'src/app/core/services/project-api.service';
import { ProjectService } from 'src/app/core/services/project.service';
import { UserApiService } from 'src/app/core/services/user-api.service';
import { UserService } from 'src/app/core/services/user.service';
import {
  Axle,
  CargoSpace,
  LoadingSide,
  LoadingSpaceType,
  Trucks,
  WorkSpace,
  WorkSpaceStop,
} from 'src/app/core/types';
import {
  emptyLoadingSpace,
  FormEvent,
} from 'src/app/main-panel/add-loading-space/add-loading-space.component';
import { LoadingSpaceDimensionsComponent } from 'src/app/main-panel/add-loading-space/components/loading-space-dimensions/loading-space-dimensions.component';
import { ModalService } from 'src/app/modal/modal.service';
import { LoadingSpaceDeleteComponent } from '../loading-space-delete/loading-space-delete.component';
import { Subscription } from 'rxjs';
import {
  convertLoadingSpaceDimensions,
  convertLoadingSpacesDimensions,
  convertSaveLoadingSpaceToCm,
} from '../../../../../shared/helpers/unitRecalculationHelpers';
import { cargoSpaceHasLoadedPalletLayers } from 'src/app/shared/helpers/pallet-loading-space.helper';

@Component({
    selector: 'gl-loading-space-edit',
    templateUrl: './loading-space-edit.component.html',
    styleUrls: ['./loading-space-edit.component.scss'],
    standalone: false
})
export class LoadingSpaceEditComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public cargoSpaces: CargoSpace[];
  @Input() public currentWorkSpaceId: number;
  @Output() public closeEditEmitter = new EventEmitter();
  @Output() public isMoreOpen = new EventEmitter();
  @ViewChild('glBox', { static: false, read: ElementRef })
  private glBoxWindow: ElementRef<HTMLElement>;
  @ViewChild('dimensions') public dimensionsComponent: LoadingSpaceDimensionsComponent;
  public project: Project;
  public currentWorkSpaceIndex: number;
  public hideDelete: boolean;
  private idLoadingSpace: number;
  public loadingSpace: LoadingSpaceType;
  public loadingSpaceSelectForm: UntypedFormGroup;
  public loadingSpaces: LoadingSpaceType[];
  public chosenType = 1;
  public chosenLoadingSpace: LoadingSpaceType;
  public isTabSet = false;
  public isTabAxle = false;
  public isTabGravityCenter = false;
  public isTabWheelArches = false;
  public isTabCooler = false;
  public isTabDoorSize = false;
  public isTabVarioFloor = false;
  public isPalletStacking = false;
  public isPalletVehicleLoading = false;
  public isAirWeightCenter = false;
  public isEdit: boolean;
  public isEditMultistop = false;
  public tabsActive = false;
  public previousTop: string;
  public savedStops: WorkSpaceStop[];
  public initialStops: WorkSpaceStop[];
  public subscriptions: Subscription[] = [];

  constructor(
    private renderer: Renderer2,
    private elRef: ElementRef<HTMLElement>,
    public projectApi: ProjectApiService,
    public projectService: ProjectService,
    private userService: UserService,
    private userApi: UserApiService,
    private cookieService: CookieService,
    private modalService: ModalService,
    private translate: TranslateService,
  ) {
    this.loadingSpace = emptyLoadingSpace;
    this.chosenLoadingSpace = emptyLoadingSpace;
    this.subscriptions.push(
      this.userService.user.pipe(pluck('trucks')).subscribe((trucks: Trucks) => {
        if (!trucks) return;

        trucks.user.forEach((truck) => (truck.isUser = true));
        this.loadingSpaces = this.sortByVolume(trucks.standard.concat(trucks.user));
      }),
    );

    this.subscriptions.push(
      this.projectService.project.subscribe((project) => {
        this.project = project;
        this.hideDelete = this.project.workSpaces.length <= 1;
      }),
    );

    this.subscriptions.push(
      this.projectService.currentWorkSpaceIndex.subscribe(
        (current) => (this.currentWorkSpaceIndex = current),
      ),
    );

    this.loadingSpaceSelectForm = this.generateLoadingSpaceSelectForm();
  }

  public ngOnInit(): void {
    this.projectService.project.subscribe((project: Project): void => {
      this.project = project;
      if (this.project.options.unit === 'mm') {
        this.loadingSpaces = convertLoadingSpacesDimensions(this.loadingSpaces, true);
      }
      const workSpace: WorkSpace = project.workSpaces.find(
        (space: WorkSpace): boolean => space.id === this.currentWorkSpaceId,
      );
      if (workSpace?.stops?.length > 0 && this.initialStops === undefined) {
        const stops = workSpace.stops.map((stop: WorkSpace) => {
          return {
            id: stop.id,
            name: stop.name,
            address: stop.address,
            country: stop.country,
            city: stop.city,
            postalCode: stop.postalCode,
          };
        });
        this.savedStops = stops;
        this.initialStops = stops.map((stop) => ({ ...stop }));
      }
      this.loadingSpaceSelectForm.controls.loadingSpaceSelect.valueChanges.subscribe(
        (loadingSpace) => {
          this.onChosen(loadingSpace);
        },
      );
      this.chosenType = this.cargoSpaces[0].type;

      this.loadingSpaces =
        this.chosenType === 3
          ? this.mapPalletHeights(this.filterLoadingSpaceByType(this.chosenType))
          : this.filterLoadingSpaceByType(this.chosenType);
      if (workSpace !== undefined && workSpace.axis !== undefined && this.cargoSpaces[0] !== undefined && this.cargoSpaces[0].axis !== undefined) {
        this.cargoSpaces[0].axis = workSpace.axis ? workSpace.axis : [];
      }
      this.setValue(this.cargoSpaces);
      if (this.palletDimensionsLocked) {
        this.loadingSpaceSelectForm.disable({ emitEvent: false });
      }
    });
  }

  public get palletDimensionsLocked(): boolean {
    return cargoSpaceHasLoadedPalletLayers(this.cargoSpaces?.[0]);
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.isEdit = true;
    }, 1);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  public openMultistop(): void {
    this.isEditMultistop = !this.isEditMultistop;
  }

  public submitStops(stops: WorkSpaceStop[]): void {
    this.savedStops = stops;
    this.openMultistop();
  }

  public getPosition(): DOMRect {
    return this.elRef.nativeElement.getBoundingClientRect();
  }

  public setValue(cargoSpace: CargoSpace[]): void {
    const isPallet =
      cargoSpace[0].dimensions.width &&
      cargoSpace[0].extraFeatures.palletHeight &&
      cargoSpace[0].dimensions.length;
    const isAirContainer =
      cargoSpace[0].extraFeatures.airType !== null &&
      cargoSpace[0].extraFeatures.airType !== undefined;
    const isValid =
      cargoSpace[0] &&
      cargoSpace[0].dimensions.length !== null &&
      cargoSpace[0].dimensions.width !== null &&
      cargoSpace[0].dimensions.height !== null;
    let loadingSpace: LoadingSpaceType;
    if (isAirContainer) {
      loadingSpace = {
        ...this.loadingSpace,
        name: cargoSpace[0].name,
        airType: cargoSpace[0].extraFeatures.airType,
        airLengthA: cargoSpace[0].extraFeatures.airLengthA,
        airWidth: cargoSpace[0].extraFeatures.airWidth,
        airBaseB: cargoSpace[0].extraFeatures.airBaseB,
        airHeightC: cargoSpace[0].extraFeatures.airHeightC,
        airHeightD: cargoSpace[0].extraFeatures.airHeightD,
      };
      this.loadingSpace = loadingSpace;
    }
    if (isPallet) {
      loadingSpace = {
        ...this.loadingSpace,
        name: cargoSpace[0].name,
        width: cargoSpace[0].dimensions.width,
        height: cargoSpace[0].extraFeatures.palletMaxHeight,
        palletHeight: cargoSpace[0].extraFeatures.palletHeight,
        length: cargoSpace[0].dimensions.length,
        palletWeight: cargoSpace[0].extraFeatures.palletWeight,
        limit: cargoSpace[0].limit,
        palletMaxHeight: cargoSpace[0].extraFeatures.palletMaxHeight,
        palletVehicle: cargoSpace[0].extraFeatures.palletVehicle,
        palletVehicleToLoad: cargoSpace[0].extraFeatures.palletVehicleToLoad,
        palletQuantity: cargoSpace[0].extraFeatures.palletQuantity,
      };
      this.loadingSpace = loadingSpace;
    }
    if (isValid) {
      loadingSpace = {
        ...this.loadingSpace,
        unit: this.project.options.unit,
        name: cargoSpace[0].name ? (this.translate.instant(cargoSpace[0].name) as string) : '',
        length: cargoSpace[0].dimensions.length,
        width: cargoSpace[0].dimensions.width,
        height: cargoSpace[0].dimensions.height,
        limit: cargoSpace[0].limit,
        trailerLoadingSide: cargoSpace[0].trailerLoadingSide,
        semiTrailerLoadingSide: cargoSpace[0].semiTrailerLoadingSide
          ? cargoSpace[0].semiTrailerLoadingSide
          : cargoSpace[0].trailerLoadingSide,
        type: cargoSpace[0].type,
        axis: this.prepareAxelsForCargoSpace(cargoSpace[0]),
        wheelTrailerWidth: cargoSpace[0].extraFeatures.wheelTrailerWidth,
        wheelTrailerHeight: cargoSpace[0].extraFeatures.wheelTrailerHeight,
        wheelTrailerLength: cargoSpace[0].extraFeatures.wheelTrailerLength,
        wheelTrailerDistance: cargoSpace[0].extraFeatures.wheelTrailerDistance,
        wheelSemiTrailerWidth: cargoSpace[1]?.extraFeatures?.wheelTrailerWidth
          ? cargoSpace[1].extraFeatures.wheelTrailerWidth
          : null,
        wheelSemiTrailerHeight: cargoSpace[1]?.extraFeatures?.wheelTrailerHeight
          ? cargoSpace[1].extraFeatures.wheelTrailerHeight
          : null,
        wheelSemiTrailerLength: cargoSpace[1]?.extraFeatures?.wheelTrailerLength
          ? cargoSpace[1].extraFeatures.wheelTrailerLength
          : null,
        wheelSemiTrailerDistance: cargoSpace[1]?.extraFeatures?.wheelTrailerDistance
          ? cargoSpace[1].extraFeatures.wheelTrailerDistance
          : null,
        coolerTrailerWidth: cargoSpace[0].extraFeatures.coolerTrailerWidth,
        coolerTrailerHeight: cargoSpace[0].extraFeatures.coolerTrailerHeight,
        coolerTrailerLength: cargoSpace[0].extraFeatures.coolerTrailerLength,
        coolerTrailerFloorHeight: cargoSpace[0].extraFeatures.coolerTrailerFloorHeight,

        coolerSemiTrailerWidth:
          cargoSpace[1] && cargoSpace[1].extraFeatures.coolerTrailerWidth
            ? cargoSpace[1].extraFeatures.coolerTrailerWidth
            : null,
        coolerSemiTrailerHeight:
          cargoSpace[1] && cargoSpace[1].extraFeatures.coolerTrailerHeight
            ? cargoSpace[1].extraFeatures.coolerTrailerHeight
            : null,
        coolerSemiTrailerLength:
          cargoSpace[1] && cargoSpace[1].extraFeatures.coolerTrailerLength
            ? cargoSpace[1].extraFeatures.coolerTrailerLength
            : null,
        coolerSemiTrailerFloorHeight:
          cargoSpace[1] && cargoSpace[1].extraFeatures.coolerTrailerFloorHeight
            ? cargoSpace[1].extraFeatures.coolerTrailerFloorHeight
            : null,

        varioTrailerFloorHeight: cargoSpace[0].extraFeatures.varioTrailerFloorHeight,
        varioSemiTrailerFloorHeight: cargoSpace[1]?.extraFeatures.varioTrailerFloorHeight
          ? cargoSpace[1]?.extraFeatures.varioTrailerFloorHeight
          : null,
        varioTrailerFloorThickness: cargoSpace[0].extraFeatures.varioTrailerFloorThickness,
        varioSemiTrailerFloorThickness: cargoSpace[1]?.extraFeatures.varioTrailerFloorThickness
          ? cargoSpace[1]?.extraFeatures.varioTrailerFloorThickness
          : null,
        varioTrailerFloorCapacity: cargoSpace[0].extraFeatures.varioTrailerFloorCapacity,
        varioSemiTrailerFloorCapacity: cargoSpace[1]?.extraFeatures.varioTrailerFloorCapacity
          ? cargoSpace[1]?.extraFeatures.varioTrailerFloorCapacity >= 0
            ? cargoSpace[1].extraFeatures.varioTrailerFloorCapacity
            : null
          : null,
        trailerDoorHeight: cargoSpace[0].extraFeatures.trailerDoorHeight,
        semiTrailerDoorHeight: cargoSpace[0].extraFeatures.semiTrailerDoorHeight
          ? cargoSpace[0].extraFeatures.semiTrailerDoorHeight
          : null,
        trailerDoorWidth: cargoSpace[0].extraFeatures.trailerDoorWidth,
        semiTrailerDoorWidth: cargoSpace[0].extraFeatures.semiTrailerDoorWidth
          ? cargoSpace[0].extraFeatures.semiTrailerDoorWidth
          : null,
        gravitySemiTrailerLengthTolerance: cargoSpace[0].extraFeatures
          .gravitySemiTrailerLengthTolerance
          ? cargoSpace[0].extraFeatures.gravitySemiTrailerLengthTolerance
          : null,
        gravityShowCenter: cargoSpace[0].extraFeatures.gravityShowCenter,
        gravityTrailerLengthTolerance: cargoSpace[0].extraFeatures.gravityTrailerLengthTolerance
          ? cargoSpace[0].extraFeatures.gravityTrailerLengthTolerance
          : null,
        gravityTrailerWidthTolerance: cargoSpace[0].extraFeatures.gravityTrailerWidthTolerance,
        trailerContainerWeight: cargoSpace[0].extraFeatures.trailerContainerWeight,
        gravitySemiTrailerWidthTolerance: cargoSpace[0].extraFeatures
          .gravitySemiTrailerWidthTolerance
          ? cargoSpace[0].extraFeatures.gravitySemiTrailerWidthTolerance
          : null,
        semiTrailerContainerWeight: cargoSpace[0].extraFeatures.semiTrailerContainerWeight
          ? cargoSpace[0].extraFeatures.semiTrailerContainerWeight
          : null,
      };
      this.loadingSpace = loadingSpace;
    }

    if (cargoSpace[1]) {
      loadingSpace = {
        ...this.loadingSpace,
        set: true,
        length1: cargoSpace[1].dimensions.length,
        width1: cargoSpace[1].dimensions.width,
        height1: cargoSpace[1].dimensions.height,
        limit1: cargoSpace[1].limit,
      };
      this.loadingSpace = loadingSpace;
    }
    this.onChosen(this.loadingSpace);
  }

  private prepareAxelsForCargoSpace(cargoSpace: CargoSpace): Axle[] {
    if (!cargoSpace.axis) {
      return [];
    }
    const modifiedAxis = cargoSpace.axis.map((axle) => {
      return {
        ...axle,
        distanceFromSpaceFront:
          axle.distanceFromSpaceFront !== undefined ? axle.distanceFromSpaceFront : axle.position.z,
      };
    });
    return modifiedAxis;
  }

  public onChosen(loadingSpace: LoadingSpaceType): void {
    this.loadingSpace = loadingSpace;
    this.chosenLoadingSpace = { ...loadingSpace };
  }

  public showTabs(): void {
    this.tabsActive = !this.tabsActive;
    if (this.tabsActive) {
      if (this.chosenType === 4) {
        this.openTab('gravity-center');
      } else if (this.chosenType === 3) {
        this.openTab('palletVehicleLoading');
      } else {
        this.openTab('set');
      }
    } else {
      this.closeTabs();
    }
    this.recalculateTop();
  }

  public recalculateTop(): void {
    setTimeout(() => {
      if (!this.previousTop) {
        this.previousTop = this.glBoxWindow.nativeElement.style.top;
      }
      if (this.tabsActive) {
        this.renderer.setStyle(
          this.glBoxWindow.nativeElement,
          'top',
          `${this.calculateGlBoxWindowTopPosition()}px`,
        );
        if (window.innerHeight <= 900) {
          this.renderer.setStyle(this.glBoxWindow.nativeElement, 'height', 'calc(100vh - 50px)');
          this.isMoreOpen.emit(true);
        }
      } else {
        if (window.innerHeight <= 900) {
          this.renderer.setStyle(this.glBoxWindow.nativeElement, 'height', 'auto');
          this.isMoreOpen.emit(false);
        }
        this.renderer.setStyle(this.glBoxWindow.nativeElement, 'top', this.previousTop);
      }
    }, 0);
  }

  public calculateGlBoxWindowTopPosition(): number {
    const glBoxRect = this.glBoxWindow.nativeElement.getBoundingClientRect();
    const elementRect = this.elRef.nativeElement.getBoundingClientRect();
    let top = elementRect.height - glBoxRect.height;
    if (this.previousTop === '0px') {
      top = 0;
    } else if (glBoxRect.height + +this.previousTop?.split('px')[0] < elementRect.height) {
      top = +this.previousTop?.split('px')[0];
    }
    if (window.innerHeight <= 900) {
      top = 0;
    }
    return top;
  }

  public axlesSave(axles: Axle[]): void {
    this.loadingSpace = {
      ...this.loadingSpace,
      axis: axles,
    };
    this.chosenLoadingSpace = {
      ...this.loadingSpace,
      axis: axles,
    }
  }

  public scrollDisable(disable: boolean): void {
    if (disable) {
      this.renderer.addClass(this.elRef.nativeElement, 'noScroll');
    } else {
      this.renderer.removeClass(this.elRef.nativeElement, 'noScroll');
    }
  }

  public deleteLoadingSpace(): void {
    const loadingSpaceDeleteObject = {
      currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpaceIndex].id,
      target: {
        workSpaceId: this.currentWorkSpaceId,
      },
    };

    const deleteOverlapPopup = this.cookieService.get('DELETE_OVERLAP_POPUP');
    const deleteLoadingSpace = () =>
      this.projectApi
        .deleteLoadingSpace(loadingSpaceDeleteObject)
        .subscribe(() => this.closeEditEmitter.emit());
    const setCurrentWorkSpace = () => {
      if (
        this.project.workSpaces.length === this.currentWorkSpaceIndex + 1 &&
        this.currentWorkSpaceIndex > 0
      ) {
        this.projectService.setCurrentWorkSpace(this.currentWorkSpaceIndex - 1);
      }
    };

    if (deleteOverlapPopup) {
      deleteLoadingSpace();
      setCurrentWorkSpace();
    } else {
      this.modalService.open(LoadingSpaceDeleteComponent).subscribe((result) => {
        if (result) {
          deleteLoadingSpace();
          setCurrentWorkSpace();
        }
      });
    }
  }

  private mapPalletHeights(loadingSpaceByType: LoadingSpaceType[]) {
    return loadingSpaceByType.map((loadingSpace) => ({
      ...loadingSpace,
      palletMaxHeight: loadingSpace.height,
    }));
  }

  private filterLoadingSpaceByType(type: number): LoadingSpaceType[] {
    return this.loadingSpaces.filter((loadingSpace) => loadingSpace.type === type);
  }

  public submitEvent(event: FormEvent, formName: string): void {
    switch (formName) {
      case 'GravityCenter': {
        const loadingSpace = {
          ...this.loadingSpace,
          gravityTrailerLengthTolerance: event.gravityTrailerLengthTolerance
            ? event.gravityTrailerLengthTolerance
            : null,
          gravitySemiTrailerLengthTolerance: event.gravitySemiTrailerLengthTolerance
            ? event.gravitySemiTrailerLengthTolerance
            : null,
          gravityShowCenter: event.gravityShowCenter,
          gravityTrailerWidthTolerance: event.gravityTrailerWidthTolerance
            ? event.gravityTrailerWidthTolerance
            : null,
          trailerContainerWeight: event.trailerContainerWeight
            ? event.trailerContainerWeight
            : null,
          gravitySemiTrailerWidthTolerance: event.gravitySemiTrailerWidthTolerance
            ? event.gravitySemiTrailerWidthTolerance
            : null,
          semiTrailerContainerWeight: event.semiTrailerContainerWeight
            ? event.semiTrailerContainerWeight
            : null,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'Door': {
        const loadingSpace = {
          ...this.loadingSpace,
          trailerDoorHeight: event.trailerDoorHeight ? parseFloat(event.trailerDoorHeight) : null,
          trailerDoorWidth: event.trailerDoorWidth ? parseFloat(event.trailerDoorWidth) : null,
          semiTrailerDoorHeight: event.semiTrailerDoorHeight
            ? parseFloat(event.semiTrailerDoorHeight)
            : null,
          semiTrailerDoorWidth: event.semiTrailerDoorWidth
            ? parseFloat(event.semiTrailerDoorWidth)
            : null,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'PalletVehicle': {
        const loadingSpace = {
          ...this.loadingSpace,
          palletVehicleToLoad:
            event.palletVehicle && event.palletVehicleToLoad ? event.palletVehicleToLoad : null,
          palletVehicle: event.palletVehicle ? event.palletVehicle : null,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'PalletStacking': {
        const loadingSpace = {
          ...this.loadingSpace,
          palletStacking: event.palletStacking,
          palletFloorNumber: event.palletFloorNumber,
          palletFloorWeight: event.palletFloorWeight,
          palletMaxWeight: event.palletMaxWeight,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'VarioFloor': {
        const loadingSpace = {
          ...this.loadingSpace,
          varioTrailerFloorHeight: event.floorHeight ? parseFloat(event.floorHeight) : null,
          varioSemiTrailerFloorHeight: event.floorHeight2 ? parseFloat(event.floorHeight2) : null,
          varioTrailerFloorThickness: event.floorThickness
            ? parseFloat(event.floorThickness)
            : null,
          varioSemiTrailerFloorThickness: event.floorThickness2
            ? parseFloat(event.floorThickness2)
            : null,
          varioTrailerFloorCapacity: event.floorMaximumCapacity
            ? parseFloat(event.floorMaximumCapacity) >= 0
              ? parseFloat(event.floorMaximumCapacity)
              : null
            : null,
          varioSemiTrailerFloorCapacity: event.floorMaximumCapacity2
            ? parseFloat(event.floorMaximumCapacity2) >= 0
              ? parseFloat(event.floorMaximumCapacity2)
              : null
            : null,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'Cooler': {
        const loadingSpace = {
          ...this.loadingSpace,
          coolerTrailerWidth: event.width ? parseFloat(event.width) : null,
          coolerTrailerHeight: event.height ? parseFloat(event.height) : null,
          coolerTrailerLength: event.length ? parseFloat(event.length) : null,
          coolerTrailerFloorHeight: event.floorHeight ? parseFloat(event.floorHeight) : null,
          coolerSemiTrailerWidth: event.width2 ? parseFloat(event.width2) : null,
          coolerSemiTrailerHeight: event.height2 ? parseFloat(event.height2) : null,
          coolerSemiTrailerLength: event.length2 ? parseFloat(event.length2) : null,
          coolerSemiTrailerFloorHeight: event.floorHeight2 ? parseFloat(event.floorHeight2) : null,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'Wheel': {
        const loadingSpace = {
          ...this.loadingSpace,
          wheelTrailerWidth: event.width ? parseFloat(event.width) : null,
          wheelTrailerHeight: event.height ? parseFloat(event.height) : null,
          wheelTrailerLength: event.length ? parseFloat(event.length) : null,
          wheelTrailerDistance: event.distance ? parseFloat(event.distance) : null,
          wheelSemiTrailerWidth: event.width2 ? parseFloat(event.width2) : null,
          wheelSemiTrailerHeight: event.height2 ? parseFloat(event.height2) : null,
          wheelSemiTrailerLength: event.length2 ? parseFloat(event.length2) : null,
          wheelSemiTrailerDistance: event.distance2 ? parseFloat(event.distance2) : null,
        };
        this.loadingSpace = loadingSpace;
        break;
      }
      case 'Set': {
        const loadingSpace = {
          ...this.loadingSpace,
          set: event.set,
          length1: event.length1 ? parseFloat(event.length1) : null,
          height1: event.height1 ? parseFloat(event.height1) : null,
          width1: event.width1 ? parseFloat(event.width1) : null,
          limit1: event.limit1 ? parseFloat(event.limit1) : null,
        };
        if (this.chosenType === 1 || this.loadingSpace.type === 1) {
          loadingSpace.semiTrailerLoadingSide = this.loadingSpace.trailerLoadingSide
            ? this.loadingSpace.trailerLoadingSide
            : 'back';
        } else {
          loadingSpace.semiTrailerLoadingSide = event.semiTrailerLoadingSide
            ? event.semiTrailerLoadingSide
            : null;
        }
        this.loadingSpace = loadingSpace;
        break;
      }
    }
  }

  public openTab(tabName: string): void {
    this.closeTabs();

    switch (tabName) {
      case 'set':
        this.isTabSet = true;
        break;
      case 'axle-loads':
        this.isTabAxle = true;
        break;
      case 'gravity-center':
        this.isTabGravityCenter = true;
        break;
      case 'wheel-arches':
        this.isTabWheelArches = true;
        break;
      case 'cooler':
        this.isTabCooler = true;
        break;
      case 'door-size':
        this.isTabDoorSize = true;
        break;
      case 'variofloor':
        this.isTabVarioFloor = true;
        break;
      case 'palletStacking':
        this.isPalletStacking = true;
        break;
      case 'palletVehicleLoading':
        this.isPalletVehicleLoading = true;
        break;
      default:
    }

    this.recalculateTop();
  }

  public closeTabs(): void {
    this.isTabSet = false;
    this.isTabAxle = false;
    this.isTabGravityCenter = false;
    this.isTabWheelArches = false;
    this.isTabCooler = false;
    this.isTabDoorSize = false;
    this.isTabVarioFloor = false;
    this.isPalletStacking = false;
    this.isPalletVehicleLoading = false;
  }

  public onPreviewClicked(event: string[]): void {
    const clickedElement = event[0];
    switch (clickedElement) {
      case 'SetBasis':
        this.openTab('set');
        break;
      case 'WheelMesh':
        this.openTab('wheel-arches');
        break;
      case 'WheelMeshSet':
        this.openTab('wheel-arches');
        break;
      case 'VarioFloor':
        this.openTab('variofloor');
        break;
      case 'VarioFloorSet':
        this.openTab('variofloor');
        break;
      case 'CoolerMesh':
        this.openTab('cooler');
        break;
      case 'CoolerMeshSet':
        this.openTab('cooler');
        break;
      case 'Axis':
        this.openTab('axle-loads');
        break;
      default:
    }
  }

  public onFormData(event: FormEvent, kind: string): void {
    const isPallet = kind === 'pallet' && event.width && event.palletHeight && event.length;
    const isAirContainer = kind === 'air' && event.airType !== null && event.airType !== undefined;
    const isValid =
      event.firstPart &&
      event.length !== null &&
      event.firstPart.width !== null &&
      event.firstPart.height !== null;
    let loadingSpace: LoadingSpaceType;
    if (isAirContainer) {
      loadingSpace = {
        ...this.loadingSpace,
        name: event.name,
        airType: event.airType,
        airLengthA: event.airLengthA,
        airWidth: event.airWidth,
        airBaseB: event.airBaseB,
        airHeightC: event.airHeightC,
        airHeightD: event.airHeightD,
        trailerLoadingSide: event.airLoadingSide ? event.airLoadingSide : 'back',
        semiTrailerLoadingSide: event.airLoadingSide ? event.airLoadingSide : 'back',
      };
      this.loadingSpace = loadingSpace;
    }
    if (isPallet) {
      loadingSpace = {
        ...this.loadingSpace,
        name: event.name,
        width: parseFloat(event.width),
        height: parseFloat(event.palletMaxHeight),
        palletHeight: parseFloat(event.palletHeight),
        length: parseFloat(event.length),
        limit: parseFloat(event.palletMaxCapacity),
        palletWeight: parseFloat(event.palletWeight),
        palletMaxHeight: parseFloat(event.palletMaxHeight),
      };
      this.loadingSpace = loadingSpace;
    }
    if (isValid) {
      loadingSpace = {
        ...this.loadingSpace,
        name: event.name ? event.name : '',
        length: parseFloat(event.firstPart.length),
        width: parseFloat(event.firstPart.width),
        height: parseFloat(event.firstPart.height),
        limit: parseFloat(event.firstPart.limit),
        trailerLoadingSide: event.firstPart.trailerLoadingSide as LoadingSide,
      };
      if (this.chosenType === 1 || this.loadingSpace.type === 1) {
        loadingSpace.semiTrailerLoadingSide = event.firstPart.trailerLoadingSide as LoadingSide;
      }
      this.loadingSpace = loadingSpace;
    }
  }

  private addAndSaveLoadingSpace(
    loadingSpaceAdd,
    loadingSpace: Partial<LoadingSpaceType>,
    shouldSave: boolean,
  ): void {
    const convertedLoadingSpace = loadingSpaceAdd;
    if (this.project.options.unit === 'mm') {
      convertedLoadingSpace.loadingSpace = convertLoadingSpaceDimensions(
        loadingSpaceAdd.loadingSpace,
        false,
      );
    }
    this.projectApi.updateLoadingSpace(convertedLoadingSpace).subscribe(() => {
      if (shouldSave) {
        let convertedSaveLoadingSpace: Partial<LoadingSpaceType> = loadingSpace;
        if (this.project.options.unit === 'mm') {
          convertedSaveLoadingSpace = convertSaveLoadingSpaceToCm(loadingSpace);
        }
        this.userApi.saveLoadingSpace(convertedSaveLoadingSpace).subscribe();
      }
      this.closeEditEmitter.emit();
    });
  }

  public submit(shouldSave: boolean): void {
    if (this.chosenType <= 2) {
      this.saveVehicle(shouldSave);
    }
    if (this.chosenType === 3) {
      this.savePallet(shouldSave);
    }
    if (this.chosenType === 4) {
      this.saveAirContainer(shouldSave);
    }
  }

  private saveVehicle(shouldSave: boolean): void {
    const trailerLoadingSide = this.loadingSpace.trailerLoadingSide
      ? this.loadingSpace.trailerLoadingSide
      : 'back';
    const semiTrailerLoadingSide =
      this.loadingSpace.set && (this.chosenType === 1 || this.loadingSpace.type === 1)
        ? trailerLoadingSide
        : this.loadingSpace.set
          ? this.loadingSpace.semiTrailerLoadingSide
          : undefined;
    const loadingSpace: Partial<LoadingSpaceType> = {
      name: this.loadingSpace.name ? this.loadingSpace.name : 'truck_without_name',
      length: parseFloat(this.dimensionsComponent.form.value.firstPart.length),
      width: parseFloat(this.dimensionsComponent.form.value.firstPart.width),
      height: parseFloat(this.dimensionsComponent.form.value.firstPart.height),
      limit: this.dimensionsComponent.form.value.firstPart.limit
        ? parseFloat(this.dimensionsComponent.form.value.firstPart.limit)
        : 0,
      set: this.loadingSpace.set,
      length1: this.loadingSpace.set ? this.loadingSpace.length1 : undefined,
      width1: this.loadingSpace.set ? this.loadingSpace.width1 : undefined,
      height1: this.loadingSpace.set ? this.loadingSpace.height1 : undefined,
      trailerLoadingSide,
      semiTrailerLoadingSide,
      axis: this.loadingSpace.axis?.length > 0 ? this.loadingSpace.axis : undefined,
      limit1: this.loadingSpace.set && this.loadingSpace.limit1 ? this.loadingSpace.limit1 : 0,
      type: this.chosenType,
    };
    const loadingSpaceAdditionalFeatures = {
      ...loadingSpace,
      coolerTrailerWidth: this.loadingSpace.coolerTrailerWidth,
      coolerTrailerHeight: this.loadingSpace.coolerTrailerHeight,
      coolerTrailerLength: this.loadingSpace.coolerTrailerLength,
      coolerTrailerFloorHeight: this.loadingSpace.coolerTrailerFloorHeight,
      coolerSemiTrailerWidth: this.loadingSpace.coolerSemiTrailerWidth,
      coolerSemiTrailerHeight: this.loadingSpace.coolerSemiTrailerHeight,
      coolerSemiTrailerLength: this.loadingSpace.coolerSemiTrailerLength,
      coolerSemiTrailerFloorHeight: this.loadingSpace.coolerSemiTrailerFloorHeight,
      wheelTrailerWidth: this.loadingSpace.wheelTrailerWidth,
      wheelTrailerHeight: this.loadingSpace.wheelTrailerHeight,
      wheelTrailerLength: this.loadingSpace.wheelTrailerLength,
      wheelTrailerDistance: this.loadingSpace.wheelTrailerDistance,
      wheelSemiTrailerWidth: this.loadingSpace.wheelSemiTrailerWidth,
      wheelSemiTrailerHeight: this.loadingSpace.wheelSemiTrailerHeight,
      wheelSemiTrailerLength: this.loadingSpace.wheelSemiTrailerLength,
      wheelSemiTrailerDistance: this.loadingSpace.wheelSemiTrailerDistance,
      varioTrailerFloorHeight: this.loadingSpace.varioTrailerFloorHeight,
      varioSemiTrailerFloorHeight: this.loadingSpace.varioSemiTrailerFloorHeight,
      varioTrailerFloorThickness: this.loadingSpace.varioTrailerFloorThickness,
      varioSemiTrailerFloorThickness: this.loadingSpace.varioSemiTrailerFloorThickness,
      varioTrailerFloorCapacity: this.loadingSpace.varioTrailerFloorCapacity,
      varioSemiTrailerFloorCapacity: this.loadingSpace.varioSemiTrailerFloorCapacity,
      trailerDoorHeight: this.loadingSpace.trailerDoorHeight,
      semiTrailerDoorHeight: this.loadingSpace.semiTrailerDoorHeight,
      trailerDoorWidth: this.loadingSpace.trailerDoorWidth,
      semiTrailerDoorWidth: this.loadingSpace.semiTrailerDoorWidth,
      gravityShowCenter: this.loadingSpace.gravityShowCenter
        ? this.loadingSpace.gravityShowCenter
        : false,
      gravityTrailerLengthTolerance: this.loadingSpace.gravityTrailerLengthTolerance,
      gravitySemiTrailerLengthTolerance: this.loadingSpace.gravitySemiTrailerLengthTolerance,
      gravityTrailerWidthTolerance: this.loadingSpace.gravityTrailerWidthTolerance,
      trailerContainerWeight: this.loadingSpace.trailerContainerWeight,
      gravitySemiTrailerWidthTolerance: this.loadingSpace.gravitySemiTrailerWidthTolerance,
      semiTrailerContainerWeight: this.loadingSpace.semiTrailerContainerWeight,
    };

    const loadingSpaceAdd = {
      currentWorkSpaceId: this.currentWorkSpaceId,
      loadingSpace: {
        ...loadingSpaceAdditionalFeatures,
        id: this.idLoadingSpace ? this.idLoadingSpace : null,
      },
      stops: this.savedStops,
      target: {
        workSpaceId: this.currentWorkSpaceId,
      },
    };
    this.addAndSaveLoadingSpace(loadingSpaceAdd, loadingSpaceAdditionalFeatures, shouldSave);
  }

  private savePallet(shouldSave: boolean): void {
    const loadingSpace: Partial<LoadingSpaceType> = {
      name: this.loadingSpace.name ? this.loadingSpace.name : 'truck_without_name',
      length: this.loadingSpace.length,
      height: this.loadingSpace.height,
      width: this.loadingSpace.width,
      limit: this.loadingSpace.limit ? this.loadingSpace.limit : 0,
      set: false,
      type: this.chosenType,
      trailerLoadingSide: this.loadingSpace.trailerLoadingSide
        ? this.loadingSpace.trailerLoadingSide
        : 'top',
      semiTrailerLoadingSide: this.loadingSpace.semiTrailerLoadingSide
        ? this.loadingSpace.semiTrailerLoadingSide
        : 'top',
      palletWeight: this.loadingSpace.palletWeight,
      palletMaxHeight: this.loadingSpace.palletMaxHeight,
      palletStacking: this.loadingSpace.palletStacking,
      palletFloorNumber: this.loadingSpace.palletFloorNumber,
      palletFloorWeight: this.loadingSpace.palletFloorWeight,
      palletVehicleToLoad: this.loadingSpace.palletVehicleToLoad
        ? this.loadingSpace.palletVehicleToLoad
        : null,
      palletVehicle: this.loadingSpace.palletVehicle ? this.loadingSpace.palletVehicle : null,
      palletMaxWeight: this.loadingSpace.palletMaxWeight ? this.loadingSpace.palletMaxWeight : null,
      palletHeight: this.loadingSpace.palletHeight ? this.loadingSpace.palletHeight : null,
      palletQuantity:
        this.loadingSpace.palletQuantity != null && this.loadingSpace.palletQuantity > 0
          ? this.loadingSpace.palletQuantity
          : 1,
    };
    const loadingSpaceAdd = {
      currentWorkSpaceId: this.currentWorkSpaceId,
      loadingSpace: {
        ...loadingSpace,
        id: this.idLoadingSpace ? this.idLoadingSpace : null,
      },
      stops: this.savedStops,
      target: {
        workSpaceId: this.currentWorkSpaceId,
      },
    };
    this.addAndSaveLoadingSpace(loadingSpaceAdd, loadingSpace, shouldSave);
  }

  private saveAirContainer(shouldSave: boolean): void {
    const loadingSpace: Partial<LoadingSpaceType> = {
      name: this.loadingSpace.name ? this.loadingSpace.name : 'truck_without_name',
      width: this.loadingSpace.airWidth,
      height: this.loadingSpace.airHeightC,
      length: this.loadingSpace.airLengthA,
      limit: this.loadingSpace.limit ? this.loadingSpace.limit : 0,
      trailerLoadingSide: this.loadingSpace.trailerLoadingSide,
      semiTrailerLoadingSide: this.loadingSpace.semiTrailerLoadingSide,
      set: false,
      type: this.chosenType,
    };

    const loadingSpaceAdditionalFeatures = {
      ...loadingSpace,
      airType: this.loadingSpace.airType,
      airLengthA: this.loadingSpace.airLengthA,
      airWidth: this.loadingSpace.airWidth,
      airBaseB: this.loadingSpace.airBaseB,
      airHeightC: this.loadingSpace.airHeightC,
      airHeightD: this.loadingSpace.airHeightD,
      gravityShowCenter:
        this.loadingSpace.gravityShowCenter === null ? false : this.loadingSpace.gravityShowCenter,
      gravityTrailerLengthTolerance: this.loadingSpace.gravityTrailerLengthTolerance,
      gravitySemiTrailerLengthTolerance: this.loadingSpace.gravitySemiTrailerLengthTolerance,
      gravityTrailerWidthTolerance: this.loadingSpace.gravityTrailerWidthTolerance,
      trailerContainerWeight: this.loadingSpace.trailerContainerWeight,
      gravitySemiTrailerWidthTolerance: this.loadingSpace.gravitySemiTrailerWidthTolerance,
      semiTrailerContainerWeight: this.loadingSpace.semiTrailerContainerWeight,
    };

    const loadingSpaceAdd = {
      currentWorkSpaceId: this.currentWorkSpaceId,
      loadingSpace: {
        ...loadingSpaceAdditionalFeatures,
        id: this.idLoadingSpace ? this.idLoadingSpace : null,
      },
      stops: this.savedStops,
      target: {
        workSpaceId: this.currentWorkSpaceId,
      },
    };
    this.addAndSaveLoadingSpace(loadingSpaceAdd, loadingSpaceAdditionalFeatures, shouldSave);
  }

  private sortByVolume(loadingSpaces: LoadingSpaceType[]) {
    return loadingSpaces.sort(
      (a, b) =>
        a.length * a.width * a.height +
        a.length1 * a.width1 * a.height1 -
        (b.length * b.width * b.height + b.length1 * b.width1 * b.height1),
    );
  }

  private generateLoadingSpaceSelectForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      loadingSpaceSelect: new UntypedFormControl(),
    });
  }
}
